import { useState, useRef } from 'react';
import {
  ButtonGroup,
  Button,
  Popover,
} from '@mui/material';
import { ArrowBack, ArrowForward, CalendarMonth } from '@mui/icons-material';
import { DateCalendar } from '@mui/x-date-pickers';
import { startOfDay } from 'date-fns';
import { getInputValueText } from './WeekPickerDialog';

interface DayPickerDialogProps {
  date: Date;
  isTimesheetLoading: boolean;
  onPreviousDay: () => void;
  onNextDay: () => void;
  onDateChange: (newDate: Date) => void;
}

export default function DayPickerDialog({
  date,
  isTimesheetLoading,
  onPreviousDay,
  onNextDay,
  onDateChange,
}: DayPickerDialogProps) {
  const [isPopperOpen, setIsPopperOpen] = useState(false);
  const inputButtonRef = useRef<HTMLButtonElement>(null);

  const closePopper = () => {
    setIsPopperOpen(false);
  };

  const handleInputClick = () => {
    setIsPopperOpen(true);
  };

  const handleDateChange = (newValue: Date | null) => {
    if (newValue) {
      onDateChange(startOfDay(newValue));
      closePopper();
    }
  };

  return (
    <ButtonGroup 
      disabled={isTimesheetLoading} 
      variant="outlined" 
      aria-label="Day navigation button group" 
      color="inherit" 
      fullWidth
    >
      <Button
        type="button"
        aria-label="Previous Day"
        onClick={onPreviousDay}
        sx={{ width: "fit-content" }}
      >
        <ArrowBack />
      </Button>
      <Button
        ref={inputButtonRef}
        sx={{ flexGrow: 2 }}
      >
        {getInputValueText(date)}
      </Button>
      <Button 
        aria-describedby="calendar-popover" 
        onClick={handleInputClick} 
        sx={{ width: "fit-content" }}
      >
        <CalendarMonth />
      </Button>
      <Popover
        id="calendar-popover"
        open={isPopperOpen}
        anchorEl={inputButtonRef.current}
        onClose={closePopper}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        classes={{ paper: 'px-2' }}
      >
        <DateCalendar
          value={date}
          onChange={handleDateChange}
        />
      </Popover>
      <Button
        type="button"
        aria-label="Next Day"
        onClick={onNextDay}
        sx={{ width: "fit-content" }}
      >
        <ArrowForward />
      </Button>
    </ButtonGroup>
  );
}
